import { createFetch } from "@better-fetch/fetch";
import environment from "@timeghost/utils/environment";
import { create, type StoreApi, type UseBoundStore } from "zustand";

export interface EntityWithId {
	readonly id: string;
}

export interface ApiBlobResult {
	readonly blob: Blob;
	readonly fileName: string | null;
	readonly headers: Headers;
}

export interface ApiClient {
	delete: <TResponse>(path: string) => Promise<TResponse>;
	get: <TResponse>(
		path: string,
		query?: Readonly<Record<string, string | number | boolean | undefined>>,
		headers?: Readonly<Record<string, string>>
	) => Promise<TResponse>;
	/** todo: add PATCH request handling to backend. for updating partial entities. */
	patch: <TResponse>(path: string, body?: unknown, headers?: Readonly<Record<string, string>>) => Promise<TResponse>;
	post: <TResponse>(path: string, body?: unknown, headers?: Readonly<Record<string, string>>) => Promise<TResponse>;
	/** POST that returns a binary download (export endpoints). */
	postBlob: (path: string, body?: unknown) => Promise<ApiBlobResult>;
}

export interface ApiAuthOptions {
	readonly getAccessToken: () => Promise<string | null>;
	readonly getGraphToken: () => Promise<string | null>;
	readonly getPrincipalId?: () => string | null | undefined;
	readonly getTimeZone?: () => string | null | undefined;
	readonly getWorkspaceId?: () => string | null | undefined;
}

export interface ApiClientOptions {
	readonly auth?: ApiAuthOptions;
	readonly baseUrl?: string;
	readonly fetchImplementation?: typeof fetch;
}

interface RequestOptions {
	readonly body?: unknown;
	readonly method: "GET" | "POST" | "DELETE" | "PATCH";
}

export interface EntityStoreMetaState {
	readonly error: string | null;
	readonly isLoading: boolean;
}

export interface EntityState<TEntity extends EntityWithId> extends EntityStoreMetaState {
	readonly entities: Readonly<Record<string, TEntity>>;
	readonly ids: readonly string[];
}

export interface EntityStoreActions<TEntity extends EntityWithId> {
	clear: () => void;
	removeMany: (ids: readonly string[]) => void;
	removeOne: (id: string) => void;
	setError: (error: string | null) => void;
	setLoading: (isLoading: boolean) => void;
	setMany: (entities: readonly TEntity[]) => void;
	upsertMany: (entities: readonly TEntity[]) => void;
	upsertOne: (entity: TEntity) => void;
}

export type EntityStore<TEntity extends EntityWithId> = UseBoundStore<
	StoreApi<EntityState<TEntity> & EntityStoreActions<TEntity>>
>;

const normalizeBaseUrl = (baseUrl: string): string => (baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl);

const resolveErrorMessage = (error: unknown): string => {
	if (error instanceof Error) {
		return error.message;
	}
	return "Request failed";
};

const buildAuthHeaders = async (auth: ApiAuthOptions | undefined): Promise<Record<string, string>> => {
	if (!auth) {
		return {};
	}

	const headers: Record<string, string> = {};
	const [accessToken, graphToken] = await Promise.all([auth.getAccessToken(), auth.getGraphToken()]);

	if (accessToken) {
		headers.Authorization = `Bearer ${accessToken}`;
	}

	if (graphToken) {
		headers["graph-token"] = graphToken;
	}

	const workspaceId = auth.getWorkspaceId?.();
	if (workspaceId) {
		headers["workspace-id"] = workspaceId;
	}

	const principalId = auth.getPrincipalId?.();
	if (principalId) {
		headers["principal-id"] = principalId;
	}

	return headers;
};

const withRequestTimeZone = (
	headers: Record<string, string>,
	getTimeZone?: () => string | null | undefined
): Record<string, string> => {
	const timeZone = getTimeZone?.() || Intl.DateTimeFormat().resolvedOptions().timeZone;
	if (!timeZone) {
		return headers;
	}
	return { ...headers, "x-request-timezone": timeZone };
};

export const createApiClient = (options: ApiClientOptions): ApiClient => {
	const normalizedBaseUrl = normalizeBaseUrl(options.baseUrl ?? environment.VITE_API_URL);
	const client = createFetch({
		baseURL: normalizedBaseUrl,
		customFetchImpl: options.fetchImplementation,
	});

	const request = async <TResponse>(
		path: string,
		config: RequestOptions,
		query?: Readonly<Record<string, string | number | boolean | undefined>>,
		extraHeaders?: Readonly<Record<string, string>>
	): Promise<TResponse> => {
		const normalizedPath = path.startsWith("/") ? path : `/${path}`;
		const authHeaders = await buildAuthHeaders(options.auth);
		// better-fetch crashes on body: null (reads .buffer in isJSONSerializable).
		const body = config.body === null || config.body === undefined ? undefined : config.body;
		const response = await client<TResponse>(normalizedPath, {
			method: config.method,
			...(body === undefined ? {} : { body }),
			query,
			headers: withRequestTimeZone(
				{
					...authHeaders,
					...(extraHeaders ?? {}),
				},
				options.auth?.getTimeZone
			),
		});
		if (response.error) {
			throw response.error;
		}
		return response.data;
	};

	const postBlob = async (path: string, body?: unknown): Promise<ApiBlobResult> => {
		const normalizedPath = path.startsWith("/") ? path : `/${path}`;
		const authHeaders = withRequestTimeZone(await buildAuthHeaders(options.auth), options.auth?.getTimeZone);
		const fetchImpl = options.fetchImplementation ?? fetch;
		const response = await fetchImpl(`${normalizedBaseUrl}${normalizedPath}`, {
			method: "POST",
			headers: {
				...authHeaders,
				"Content-Type": "application/json",
				Accept: "*/*",
			},
			body: body === undefined ? undefined : JSON.stringify(body),
		});
		if (!response.ok) {
			const message = await response.text().catch(() => "");
			throw new Error(message || `Export failed (${response.status})`);
		}
		const blob = await response.blob();
		const fileName =
			response.headers.get("x-export-filename") ??
			extractFilenameFromContentDisposition(response.headers.get("content-disposition"));
		return { blob, fileName, headers: response.headers };
	};

	return {
		get: <TResponse>(
			path: string,
			query?: Readonly<Record<string, string | number | boolean | undefined>>,
			headers?: Readonly<Record<string, string>>
		) => request<TResponse>(path, { method: "GET" }, query, headers),
		post: <TResponse>(path: string, body?: unknown, headers?: Readonly<Record<string, string>>) =>
			request<TResponse>(path, { method: "POST", body }, undefined, headers),
		postBlob,
		patch: <TResponse>(path: string, body?: unknown, headers?: Readonly<Record<string, string>>) =>
			request<TResponse>(path, { method: "PATCH", body }, undefined, headers),
		delete: <TResponse>(path: string) => request<TResponse>(path, { method: "DELETE" }),
	};
};

const extractFilenameFromContentDisposition = (header: string | null): string | null => {
	if (!header) {
		return null;
	}
	const star = header.match(/filename\*\s*=\s*(?:UTF-8''|utf-8'')?([^;]+)/i);
	const plain = header.match(/filename\s*=\s*"?([^";]+)"?/i);
	const raw = (star?.[1] ?? plain?.[1] ?? "").trim().replace(/^["']|["']$/g, "");
	if (!raw) {
		return null;
	}
	try {
		return decodeURIComponent(raw);
	} catch {
		return raw;
	}
};

const toEntityMap = <TEntity extends EntityWithId>(entities: readonly TEntity[]): Record<string, TEntity> => {
	const record: Record<string, TEntity> = {};
	entities.forEach((entity) => {
		record[entity.id] = entity;
	});
	return record;
};

export const createEntityStore = <TEntity extends EntityWithId>(): EntityStore<TEntity> =>
	create<EntityState<TEntity> & EntityStoreActions<TEntity>>((set) => ({
		ids: [],
		entities: {},
		isLoading: false,
		error: null,
		setMany: (entities: readonly TEntity[]) => {
			set(() => ({
				ids: entities.map((entity) => entity.id),
				entities: toEntityMap(entities),
			}));
		},
		upsertMany: (entities: readonly TEntity[]) => {
			set((currentState) => {
				const nextEntities: Record<string, TEntity> = { ...currentState.entities };
				const idSet = new Set(currentState.ids);

				entities.forEach((entity) => {
					nextEntities[entity.id] = entity;
					idSet.add(entity.id);
				});

				return {
					entities: nextEntities,
					ids: Array.from(idSet),
				};
			});
		},
		upsertOne: (entity: TEntity) => {
			set((currentState) => {
				const exists = currentState.ids.includes(entity.id);
				return {
					entities: {
						...currentState.entities,
						[entity.id]: entity,
					},
					ids: exists ? currentState.ids : [...currentState.ids, entity.id],
				};
			});
		},
		removeOne: (id: string) => {
			set((currentState) => {
				const nextEntities = { ...currentState.entities };
				delete nextEntities[id];

				return {
					entities: nextEntities,
					ids: currentState.ids.filter((entityId) => entityId !== id),
				};
			});
		},
		removeMany: (ids: readonly string[]) => {
			const idSet = new Set(ids);
			set((currentState) => {
				const nextEntities = { ...currentState.entities };
				ids.forEach((id) => {
					delete nextEntities[id];
				});

				return {
					entities: nextEntities,
					ids: currentState.ids.filter((entityId) => !idSet.has(entityId)),
				};
			});
		},
		setLoading: (isLoading: boolean) => {
			set(() => ({ isLoading }));
		},
		setError: (error: string | null) => {
			set(() => ({ error }));
		},
		clear: () => {
			set(() => ({
				ids: [],
				entities: {},
				isLoading: false,
				error: null,
			}));
		},
	}));

export const runStoreAction = async <TResult>(
	setLoading: (isLoading: boolean) => void,
	setError: (error: string | null) => void,
	action: () => Promise<TResult>
): Promise<TResult> => {
	setLoading(true);
	setError(null);

	try {
		const result = await action();
		setLoading(false);
		return result;
	} catch (error: unknown) {
		setLoading(false);
		setError(resolveErrorMessage(error));
		throw error;
	}
};
